import { conceptsService } from '@/shared/api/services/concepts.service';
import { findResumableDraftConcept } from '@/features/concepts-overview/lib/resolve-add-concept';
import { ConceptAngleType } from '@/shared/constants';
import type { ConceptAngle } from '@/types/concept';

export interface MainConceptAngle {
  conceptId: string;
  conceptAngleId: string;
}

function findMain(conceptAngles: ConceptAngle[]): ConceptAngle | undefined {
  return conceptAngles.find(
    (ca) => ca.attributes.type === ConceptAngleType.MAIN
  );
}

/**
 * The empty draft to render on, when one already exists for this photo.
 *
 * Same rule as the "Create concept" entry points (`findResumableDraftConcept`:
 * the newest draft with no main render), narrowed to a draft whose main view
 * IS this photo. A wizard that is resumed, or relaunched after a refusal, must
 * reuse its own draft instead of piling up a new empty design each time.
 */
export async function findResumableDraftForAngle(
  projectId: string,
  angleId: string
): Promise<MainConceptAngle | null> {
  const draft = findResumableDraftConcept(
    await conceptsService.listConcepts(projectId)
  );
  if (!draft) return null;

  const { data } = await conceptsService.getConceptAngles(draft.id);
  const main = findMain(data);
  if (!main || main.relationships?.angle?.data?.id !== angleId) return null;

  return { conceptId: draft.id, conceptAngleId: main.id };
}

/** The main concept-angle of a concept, where its main render is created. */
export async function loadMainConceptAngle(
  conceptId: string
): Promise<MainConceptAngle> {
  const { data } = await conceptsService.getConceptAngles(conceptId);
  const main = findMain(data);
  if (!main) {
    throw new Error(`Concept ${conceptId} has no main view`);
  }
  return { conceptId, conceptAngleId: main.id };
}
